<template>
  <Filters
    :initial-values
    :page-type="ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS"
    :status-filter-option="openTasksStatusOptions"
    @apply-filters="handleApplyFilter"
  />

  <template v-if="shouldDisplayCustomerCareDashboardLinks">
    <RevDivider />

    <div class="my-32 flex space-x-12">
      <RevButton
        size="small"
        target="_blank"
        :to="returnDetailsLinkValue"
        variant="secondary"
        @click="trackClickOnReturnDetailsButton"
      >
        {{ i18n(translations.returnDetailsButton) }}
      </RevButton>
      <RevLink @click="openReturnDetailsModal">
        {{ i18n(translations.returnDetailsOpenModalButton) }}
      </RevLink>
    </div>
  </template>

  <RevInfoBlock
    class="body-2-bold mb-32"
    :icon="IconInfo"
    :title="i18n(translations.infoBlockContent)"
    variant="info"
  />

  <RevDrawer
    backdrop-z-index="z-30"
    :name="modalName"
    :title="i18n(translations.returnDetailsModalHeader)"
  >
    <template #body>
      <div class="text-action-default-hi space-y-24">
        <div class="font-weight-heading-1 heading-1">
          {{ i18n(translations.returnDetailsModalTitle) }}
        </div>
        <div class="font-weight-body-1 text-body-1">
          {{ i18n(translations.returnDetailsModalDescription) }}
        </div>
        <RevTextList variant="ordered">
          <RevTextListItem>
            <FormattedMessage
              :definition="
                translations.returnDetailsModalGuideStepOneFirstParagraph
              "
            >
              <template #returndetailslink>
                <RevLink
                  target="_blank"
                  :to="returnDetailsLinkValue"
                  underlined
                  @click="trackClickOnReturnDetailsLink"
                >
                  {{
                    i18n(translations.returnDetailsModalGuideStepOneLinkLabel)
                  }}
                </RevLink>
              </template>
            </FormattedMessage>
            <div class="mt-24">
              {{
                i18n(translations.returnDetailsModalGuideStepOneSecondParagraph)
              }}
            </div>
          </RevTextListItem>
          <RevTextListItem>{{
            i18n(translations.returnDetailsModalGuideStepTwo)
          }}</RevTextListItem>
          <RevTextListItem>{{
            i18n(translations.returnDetailsModalGuideStepThree)
          }}</RevTextListItem>
        </RevTextList>
      </div>
    </template>
  </RevDrawer>

  <RevCard>
    <RevLoadingScreen
      v-if="isLoading"
      class="py-72"
      :text="i18n(translations.loadingScreenLabel)"
    />
    <div v-if="!isLoading">
      <div v-if="!hasOpenTasks">
        <NoRequestFoundBlock v-if="noRequestFoundAfterFiltering">
          <RevLink :to="allRequestsLink">
            <span class="body-1-bold">
              {{ i18n(translations.noRequestFoundLinkLabel) }}
            </span>
          </RevLink>
        </NoRequestFoundBlock>
        <NoOpenTaskBlock v-if="noOpenTasks" />
      </div>
      <div v-if="hasOpenTasks && !error" class="space-y-16 py-16">
        <h2 class="text-heading-2 px-16">
          {{
            i18n(translations.taskTotalCount, {
              count: payload?.metadata?.totalCount,
            })
          }}
        </h2>
        <OpenTasksTable :requests />

        <RevPagination
          class="py-24"
          :current-page
          :current-page-label="
            i18n(translations.currentPage, { page: currentPage })
          "
          :navigation-aria-label="i18n(translations.navigationLabel)"
          :next-aria-label="i18n(translations.nextPage)"
          :page-aria-label="
            (page) => i18n(translations.paginationLabel, { page })
          "
          :page-count
          :previous-aria-label="i18n(translations.previousPage)"
          @click="handlePagination"
        />
      </div>
      <TechnicalIssueBlock
        v-if="error"
        :title="i18n(translations.technicalIssueBlockTitle)"
        @try-again="refresh"
      />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData, useRoute, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { mapValues } from '@backmarket/utils/object/mapValues'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { openModal } from '@ds/components/ModalBase'
import { RevPagination } from '@ds/components/Pagination'
import { RevTextList } from '@ds/components/TextList'
import { RevTextListItem } from '@ds/components/TextListItem'
import { IconInfo } from '@ds/icons/IconInfo'

import type { RequestStatusType } from '~/api/seller-after-sales/types'
import type { Filter } from '~/api/seller-after-sales/types/requests.types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { MODAL_NAMES } from '~/scopes/customer-care/modalNames'
import { useFormattedQueryParams } from '~/scopes/customer-care/pages/customer-care/composables/useFormattedQueryParams'

import {
  EMPTY_FILTERS_VALUES,
  MULTISELECT_FILTERS_NAMES,
} from '../Filters/Filters.constants'
import filtersTranslations from '../Filters/Filters.translations'
import Filters from '../Filters/Filters.vue'
import NoRequestFoundBlock from '../NoRequestFoundBlock/NoRequestFoundBlock.vue'
import TechnicalIssueBlock from '../TechnicalIssueBlock/TechnicalIssueBlock.vue'

import translations from './OpenTasks.translations'
import NoOpenTaskBlock from './components/NoOpenTaskBlock/NoOpenTaskBlock.vue'
import OpenTasksTable from './components/OpenTasksTable/OpenTasksTable.vue'

const i18n = useI18n()
const tracking = useTracking()
const $httpFetch = useHttpFetch()
const DEFAULT_CURRENT_PAGE = 1
const REQUEST_COUNT_PER_PAGE = 20
const route = useRoute()
const currentPage = ref(
  parseInt(route.query.page?.toString() || '', 10) || DEFAULT_CURRENT_PAGE,
)

const emit = defineEmits(['update-task-count'])

const returnDetailsLinkValue = {
  name: ROUTE_NAMES.INSIGHTS.INDEX,
  query: {
    tableau: 'customer-care',
    subview: 'details',
    view: 'Returns',
  },
}

const modalName = MODAL_NAMES.RETURN_DETAILS

function openReturnDetailsModal() {
  openModal(modalName)
  tracking.trackClick({
    name: 'return_details_open_drawer_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
  })
}

function trackClickOnReturnDetailsButton() {
  tracking.trackClick({
    name: 'return_details_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
  })
}

function trackClickOnReturnDetailsLink() {
  tracking.trackClick({
    name: 'return_details_drawer_link_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
  })
}

const allRequestsLink = {
  name: ROUTE_NAMES.CUSTOMER_CARE.ALL_REQUESTS,
}

const formattedQueryParams = computed(() => {
  return mapValues(route.query, (value, key) =>
    MULTISELECT_FILTERS_NAMES.includes(key.toString())
      ? value?.toString().split(',')
      : value,
  )
})

const queryParams = computed(() => ({
  sort: 'DEADLINE',
  pageNumber: currentPage.value,
  pageSize: REQUEST_COUNT_PER_PAGE,
  status: 'TODO',
  ...formattedQueryParams.value,
}))

const {
  data: payload,
  status,
  error,
  refresh,
} = useAsyncData(
  () =>
    $httpFetch('/seller-after-sales/api/v1/requests', {
      query: queryParams.value,
    }),
  {
    lazy: true,
    watch: [queryParams],
  },
)

const isLoading = computed(() => status.value === 'pending')

emit('update-task-count')

const openTasksStatusOptions = computed(() => {
  return [
    {
      label: i18n(filtersTranslations.statusFilterLateOption),
      value: 'LATE' as RequestStatusType,
    },
  ]
})

const requests = computed(() => payload.value?.requests || [])

const noRequestFoundAfterFiltering = computed(() => {
  return !isEmpty(formattedQueryParams.value) && !error?.value
})

const noOpenTasks = computed(() => {
  return (
    payload?.value?.metadata?.totalCount === 0 &&
    isEmpty(formattedQueryParams?.value) &&
    !error?.value
  )
})

const hasOpenTasks = computed(() => !isEmpty(requests.value))

const initialValues = computed(() => {
  return { ...EMPTY_FILTERS_VALUES, ...formattedQueryParams.value }
})

async function handleApplyFilter(filters: Filter) {
  currentPage.value = DEFAULT_CURRENT_PAGE
  const nonEmptyQueryParams = ref(useFormattedQueryParams(filters))

  navigateTo({
    name: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
    query: nonEmptyQueryParams.value,
  })
}

const pageCount = computed(() =>
  Math.ceil(
    (payload?.value?.metadata?.totalCount ?? 0) / REQUEST_COUNT_PER_PAGE,
  ),
)

function handlePagination({ page }: { page: number }) {
  tracking.trackPaginationClicked({
    name: 'pagination_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
    current_page: currentPage.value,
    target_page: page,
  })

  if (currentPage.value !== page) {
    currentPage.value = page
    navigateTo({
      name: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
      query: {
        ...route.query,
        page: currentPage.value,
      },
    })
  }
}

const {
  FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_COUNTRY,
  FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_SELLER_ID,
} = useRuntimeConfig().public
const { market } = useMarketplace()
const { publicId } = useSellerInfoStore()

const shouldDisplayCustomerCareDashboardLinks = computed(() => {
  const hiddenCountries = (
    FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_COUNTRY || ''
  )
    .toString()
    .split(',')

  const hiddenSellerIds = (
    FF_HIDE_SELLER_CUSTOMER_CARE_DASHBOARD_BY_SELLER_ID || ''
  )
    .toString()
    .split(',')

  const isHiddenForCurrentSeller = publicId
    ? hiddenSellerIds.includes(publicId.toString())
    : false

  return (
    !hiddenCountries.includes(market.countryCode) && !isHiddenForCurrentSeller
  )
})
</script>
